import { IScene } from "@layerhub-io/types"
import React from "react"
import { ContextMenuSceneRequest, ContextMenuTimelineRequest, DesignType, IDesign } from "~/interfaces/DesignEditor"
import Cookies from 'js-cookie'

interface ISceneEditorContext {
  scenes: IScene[]
  setScenes: (value: ((prevState: IScene[]) => IScene[]) | IScene[]) => void
  currentScene: IScene | null
  setCurrentScene: React.Dispatch<React.SetStateAction<IScene | null>>
  currentDesign: IDesign
  setCurrentDesign: React.Dispatch<React.SetStateAction<IDesign>>
  isSidebarOpen: boolean
  setIsSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  editorType: DesignType
  setEditorType: React.Dispatch<React.SetStateAction<DesignType>>
  displayPlayback: boolean
  setDisplayPlayback: React.Dispatch<React.SetStateAction<boolean>>
  displayPreview: boolean
  setCropModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  cropModalOpen: boolean,
  setExtrasModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  errorModalOpen: boolean,
  setErrorModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
  extrasModalOpen: boolean,
  cropImage: any
  setCropImage: React.Dispatch<React.SetStateAction<any>>
  setDisplayPreview: React.Dispatch<React.SetStateAction<boolean>>
  currentPreview: string
  setCurrentPreview: React.Dispatch<React.SetStateAction<string>>
  maxTime: number
  setMaxTime: React.Dispatch<React.SetStateAction<number>>
  contextMenuTimelineRequest: ContextMenuTimelineRequest
  setContextMenuTimelineRequest: React.Dispatch<React.SetStateAction<ContextMenuTimelineRequest>>
  contextMenuSceneRequest: ContextMenuTimelineRequest
  setContextMenuSceneRequest: React.Dispatch<React.SetStateAction<ContextMenuTimelineRequest>>
  showDottedLine: boolean
  setShowDottedLine: React.Dispatch<React.SetStateAction<boolean>>
}

export const DesignEditorContext = React.createContext<ISceneEditorContext>({
  scenes: [],
  setScenes: () => { },
  currentScene: null,
  setCurrentScene: () => { },
  currentDesign: {
    id: "",
    frame: {
      width: 1,
      height: 1,
    },
    metadata: {},
    name: "",
    preview: "",
    scenes: [],
    type: "",
  },
  setCurrentDesign: () => { },
  isSidebarOpen: true,
  setIsSidebarOpen: () => { },
  editorType: "GRAPHIC",
  setEditorType: () => { },
  displayPlayback: false,
  setDisplayPlayback: () => { },
  displayPreview: false,
  setDisplayPreview: () => { },
  currentPreview: "",
  setCropModalOpen: () => { },
  setExtrasModalOpen: () => { },
  errorModalOpen: false,
  setErrorModalOpen: () => { },
  extrasModalOpen: false,
  cropModalOpen: false,
  cropImage: {},
  setCropImage: () => { },
  setCurrentPreview: () => { },
  maxTime: 0,
  setMaxTime: () => { },
  contextMenuTimelineRequest: {
    id: "",
    left: 0,
    top: 0,
    visible: false,
  },
  setContextMenuTimelineRequest: () => { },
  contextMenuSceneRequest: {
    id: "",
    left: 0,
    top: 0,
    visible: false,
  },
  setContextMenuSceneRequest: () => { },
  showDottedLine: false,
  setShowDottedLine: () => { },

})

export const DesignEditorProvider = ({ children }: { children: React.ReactNode }) => {
  // Initialize currentDesign.id from cookie if available
  const getInitialDesignId = () => {
    const activeDesignId = Cookies.get('activeDesignId')
    return activeDesignId || ""
  }

  const [scenes, setScenes] = React.useState<IScene[]>([])
  const [currentScene, setCurrentScene] = React.useState<IScene | null>(null)
  const [currentDesign, setCurrentDesign] = React.useState<IDesign>({
    id: getInitialDesignId(),
    frame: {
      width: 1,
      height: 1,
    },
    metadata: {},
    name: "",
    preview: "",
    scenes: [],
    type: "",
  })
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true)
  const [editorType, setEditorType] = React.useState<DesignType>("GRAPHIC")
  const [displayPlayback, setDisplayPlayback] = React.useState<boolean>(false)
  const [displayPreview, setDisplayPreview] = React.useState<boolean>(false)
  const [cropModalOpen, setCropModalOpen] = React.useState<boolean>(false)
  const [extrasModalOpen, setExtrasModalOpen] = React.useState<boolean>(false)
  const [errorModalOpen, setErrorModalOpen] = React.useState<boolean>(false)
  const [cropImage, setCropImage] = React.useState<any>({})
  const [currentPreview, setCurrentPreview] = React.useState<string>("")
  const [maxTime, setMaxTime] = React.useState(5000)
  const [contextMenuTimelineRequest, setContextMenuTimelineRequest] = React.useState<ContextMenuTimelineRequest>({
    id: "",
    left: 0,
    top: 0,
    visible: false,
  })
  const [contextMenuSceneRequest, setContextMenuSceneRequest] = React.useState<ContextMenuSceneRequest>({
    id: "",
    left: 0,
    top: 0,
    visible: false,
  })
  const [showDottedLine, setShowDottedLine] = React.useState<boolean>(false)

  // Update currentDesign.id when the activeDesignId cookie is set
  React.useEffect(() => {
    const checkForDesignId = () => {
      const activeDesignId = Cookies.get('activeDesignId')
      if (activeDesignId && currentDesign.id !== activeDesignId) {
        setCurrentDesign(prev => ({
          ...prev,
          id: activeDesignId
        }))
      }
    }

    // Check immediately
    checkForDesignId()

    // Set up an interval to check periodically until the ID is set
    const intervalId = setInterval(() => {
      const activeDesignId = Cookies.get('activeDesignId')
      if (activeDesignId) {
        checkForDesignId()
        clearInterval(intervalId)
      }
    }, 100)

    return () => clearInterval(intervalId)
  }, [])

  const context = {
    scenes,
    setScenes,
    currentScene,
    setCurrentScene,
    currentDesign,
    setCurrentDesign,
    isSidebarOpen,
    setIsSidebarOpen,
    editorType,
    setEditorType,
    displayPlayback,
    setDisplayPlayback,
    displayPreview,
    setDisplayPreview,
    cropModalOpen,
    setCropModalOpen,
    extrasModalOpen,
    setExtrasModalOpen,
    errorModalOpen,
    setErrorModalOpen,
    cropImage,
    setCropImage,
    currentPreview,
    setCurrentPreview,
    maxTime,
    setMaxTime,
    contextMenuTimelineRequest,
    setContextMenuTimelineRequest,
    contextMenuSceneRequest,
    setContextMenuSceneRequest,
    showDottedLine,
    setShowDottedLine,
  }
  return <DesignEditorContext.Provider value={context}>{children}</DesignEditorContext.Provider>
}
